import axios, { AxiosInstance, AxiosResponse } from 'axios';

export interface User {
  id: string;
  name: string;
  email: string;
  role: 'DEVELOPER' | 'ADMIN' | 'MANAGER' | 'OPERATOR' | 'VIEWER';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateUserRequest {
  name: string;
  email: string;
  password: string;
  role: 'DEVELOPER' | 'ADMIN' | 'MANAGER' | 'OPERATOR' | 'VIEWER';
}

export interface UpdateUserRequest {
  name?: string;
  role?: 'DEVELOPER' | 'ADMIN' | 'MANAGER' | 'OPERATOR' | 'VIEWER';
  isActive?: boolean;
}

class UserService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3000/api',
      timeout: 10000,
    });

    // Interceptor para adicionar token JWT
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
  }

  async createUser(userData: CreateUserRequest): Promise<User> {
    try {
      const response: AxiosResponse<User> = await this.client.post('/admin/users', userData);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao criar usuário');
    }
  }

  async listUsers(): Promise<User[]> {
    try {
      const response: AxiosResponse<User[]> = await this.client.get('/admin/users');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao listar usuários');
    }
  }

  async getUserById(userId: string): Promise<User> {
    try {
      const response: AxiosResponse<User> = await this.client.get(`/admin/users/${userId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao buscar usuário');
    }
  }

  async updateUser(userId: string, userData: UpdateUserRequest): Promise<User> {
    try {
      const response: AxiosResponse<User> = await this.client.put(`/admin/users/${userId}`, userData);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao atualizar usuário');
    }
  }

  async deleteUser(userId: string): Promise<void> {
    try {
      await this.client.delete(`/admin/users/${userId}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao deletar usuário');
    }
  }
}

export const userService = new UserService();
export default userService;
